import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Layout } from '../../components/Layout.js';
import { api, ApiClientError, setAccessToken } from '../../lib/api.js';
import { useAuthStore, type SessionUser } from '../../lib/auth.js';
import { setUserId, trackMagicLinkVerified } from '../../lib/analytics.js';

interface VerifyResponse {
  accessToken: string;
  user: SessionUser;
  isNewUser?: boolean;
  redirectTo?: string;
}

export function VerifyMagicLinkPage() {
  const [params] = useSearchParams();
  const { lang } = useParams();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const [status, setStatus] = useState<'verifying' | 'expired' | 'invalid' | 'verified'>(
    'verifying',
  );

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('invalid');
      return;
    }
    api<VerifyResponse>('/auth/verify', { method: 'POST', body: { token } })
      .then((res) => {
        setAccessToken(res.accessToken);
        setUser(res.user);
        setUserId(res.user.id);
        trackMagicLinkVerified(res.isNewUser === true);
        setStatus('verified');
        const fallback = `/${lang}/dashboard`;
        let target = fallback;
        if (res.redirectTo) {
          try {
            const parsed = new URL(res.redirectTo, window.location.origin);
            // Solo aceptamos redirects al mismo origen (open-redirect guard).
            if (parsed.origin === window.location.origin) {
              target = `${parsed.pathname}${parsed.search}${parsed.hash}`;
            }
          } catch {
            /* invalid URL → usamos fallback */
          }
        }
        navigate(target, { replace: true });
      })
      .catch((err: ApiClientError) => {
        setStatus(err.message?.includes('expired') ? 'expired' : 'invalid');
      });
  }, [params, navigate, lang, setUser]);

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 pt-32 text-center">
        {status === 'verifying' && <p className="text-neutral-400">{t('magic_link.verifying')}</p>}
        {status === 'verified' && <p className="text-neutral-400">{t('magic_link.verified')}</p>}
        {status === 'expired' && (
          <>
            <h1 className="text-2xl font-bold mb-2">{t('magic_link.expired_title')}</h1>
            <p className="text-neutral-400">{t('magic_link.expired_body')}</p>
          </>
        )}
        {status === 'invalid' && (
          <>
            <h1 className="text-2xl font-bold mb-2">{t('magic_link.invalid_title')}</h1>
            <p className="text-neutral-400">{t('magic_link.invalid_body')}</p>
          </>
        )}
      </div>
    </Layout>
  );
}
